import { create } from 'zustand';
import type { CreaseAssignment, CreasePattern, SelectionRef } from '@/origami/types';
import { clonePattern } from '@/origami/types';
import { planarize } from '@/origami/planarize';
import { analyzePattern, type PatternAnalysis } from '@/origami-math/analysis';
import { blankSheet } from '@/examples/builders';
import { EXAMPLES, getExample } from '@/examples/library';
import { defaultCompliantParams } from '@/simulation/compliant';
import { defaultRigidOptions } from '@/simulation/rigid';
import type { EngineParams, SimulationFrame, SolverMode } from '@/simulation/engine';
import type { Vec2 } from '@/geometry/vec2';
import { MAX_FOLD_ANGLE, clamp } from '@/constants/numeric';
import { resolveTargetAngle } from '@/origami/angles';
import { clipSegmentToPolygon } from '@/origami/constructions';

export type ToolId =
  | 'select'
  | 'crease'
  | 'mountain'
  | 'valley'
  | 'flat'
  | 'cut'
  | 'erase'
  | 'move'
  | 'measure';

export type ColorMode = 'normal' | 'strain' | 'constraint' | 'orientation';
export type LayoutMode = '2d' | 'split' | '3d';

export interface SnapSettings {
  enabled: boolean;
  vertices: boolean;
  midpoints: boolean;
  intersections: boolean;
  edges: boolean;
  grid: boolean;
  gridDivisions: number;
  axes: boolean;
  angles: boolean;
  angleStepDegrees: number;
}

export interface DebugSettings {
  vertexIds: boolean;
  edgeIds: boolean;
  faceIds: boolean;
  faceNormals: boolean;
  hingeAxes: boolean;
  triangulation: boolean;
  collisionBoxes: boolean;
  velocities: boolean;
}

export interface ViewSettings {
  layout: LayoutMode;
  splitRatio: number;
  theme: 'dark' | 'light';
  showGrid: boolean;
  showAuxiliary: boolean;
  showCreaseLines: boolean;
  wireframe: boolean;
  transparent: boolean;
  shadows: boolean;
  colorMode: ColorMode;
  orthographic: boolean;
  debug: DebugSettings;
  showDebugPanel: boolean;
}

export interface SimSettings {
  mode: SolverMode;
  foldAmount: number;
  playing: boolean;
  playDirection: 1 | -1;
  speed: number;
  collisionEnabled: boolean;
  edgeTargetOverride: Record<number, number>;
  lockedEdges: number[];
  compliant: ReturnType<typeof defaultCompliantParams>;
  rigid: ReturnType<typeof defaultRigidOptions>;
}

export interface Notice {
  id: number;
  severity: 'info' | 'warning' | 'error';
  message: string;
}

interface HistoryEntry {
  pattern: CreasePattern;
  label: string;
}

export interface AppState {
  pattern: CreasePattern;
  analysis: PatternAnalysis;
  past: HistoryEntry[];
  future: HistoryEntry[];
  lastAction: string;

  tool: ToolId;
  selection: SelectionRef[];
  hover: SelectionRef | null;
  /** Pattern edge whose crease inspector is open. */
  inspectedEdge: number | null;
  inspectedVertex: number | null;

  snap: SnapSettings;
  view: ViewSettings;
  sim: SimSettings;

  /** Vertex the user is dragging in the 3D view, as a simulation-mesh index. */
  pin: { index: number; x: number; y: number; z: number } | null;
  frame: SimulationFrame | null;
  fps: number;
  notices: Notice[];
  learnTopic: string | null;

  // --- actions ---
  commit: (pattern: CreasePattern, label: string) => void;
  replaceWithoutHistory: (pattern: CreasePattern) => void;
  undo: () => void;
  redo: () => void;

  setTool: (tool: ToolId) => void;
  setSelection: (selection: SelectionRef[]) => void;
  toggleSelection: (ref: SelectionRef, additive: boolean) => void;
  setHover: (ref: SelectionRef | null) => void;
  inspectEdge: (edge: number | null) => void;
  inspectVertex: (vertex: number | null) => void;

  addCrease: (a: Vec2, b: Vec2, assignment: CreaseAssignment) => void;
  assignSelection: (assignment: CreaseAssignment) => void;
  toggleMountainValley: () => void;
  deleteSelection: () => void;
  moveVertex: (index: number, to: Vec2, commit: boolean) => void;

  setEdgeTarget: (edge: number, radians: number | null) => void;
  toggleEdgeLock: (edge: number) => void;

  newSheet: (width: number, height: number) => void;
  loadExample: (id: string) => void;
  loadPattern: (pattern: CreasePattern, label: string) => void;

  setSnap: (patch: Partial<SnapSettings>) => void;
  setView: (patch: Partial<ViewSettings>) => void;
  setDebug: (patch: Partial<DebugSettings>) => void;
  setSim: (patch: Partial<SimSettings>) => void;
  setFoldAmount: (amount: number) => void;
  resetSimulation: () => void;

  setPin: (pin: { index: number; x: number; y: number; z: number } | null) => void;
  setFrame: (frame: SimulationFrame) => void;
  setFps: (fps: number) => void;
  notify: (severity: Notice['severity'], message: string) => void;
  dismissNotice: (id: number) => void;

  engineParams: () => EngineParams;
}

const HISTORY_LIMIT = 100;
let noticeId = 1;

const defaultSnap = (): SnapSettings => ({
  enabled: true,
  vertices: true,
  midpoints: true,
  intersections: true,
  edges: true,
  grid: true,
  gridDivisions: 8,
  axes: true,
  angles: true,
  angleStepDegrees: 15,
});

const defaultView = (): ViewSettings => ({
  layout: 'split',
  splitRatio: 0.42,
  theme: 'dark',
  showGrid: true,
  showAuxiliary: false,
  showCreaseLines: true,
  wireframe: false,
  transparent: false,
  shadows: true,
  colorMode: 'normal',
  orthographic: false,
  showDebugPanel: false,
  debug: {
    vertexIds: false,
    edgeIds: false,
    faceIds: false,
    faceNormals: false,
    hingeAxes: false,
    triangulation: false,
    collisionBoxes: false,
    velocities: false,
  },
});

/**
 * The pattern the app opens with. Miura-ori is the showcase: it is genuinely
 * rigid-foldable with a single degree of freedom, so one slider deploys the
 * whole tessellation and every panel has something real to say about it.
 */
const DEFAULT_EXAMPLE_ID = 'miura-ori';
const defaultExample = getExample(DEFAULT_EXAMPLE_ID);

const defaultSim = (): SimSettings => ({
  // Match the opening example's recommended solver, the same way loadExample
  // does, so the app does not start in a mode its own default pattern is not
  // suited to.
  mode: defaultExample?.recommendedSolver ?? 'compliant',
  foldAmount: 0,
  playing: false,
  playDirection: 1,
  speed: 0.5,
  collisionEnabled: false,
  edgeTargetOverride: {},
  lockedEdges: [],
  compliant: defaultCompliantParams(),
  rigid: defaultRigidOptions(),
});

/**
 * The outline of the sheet: the largest cycle bounding the planar graph from
 * outside. Empty when the pattern encloses nothing, in which case clipping is
 * skipped rather than throwing everything away.
 */
function paperOutline(state: { analysis: PatternAnalysis; pattern: CreasePattern }): Vec2[] {
  const cycles = state.analysis.topology.planar.outerCycles;
  if (cycles.length === 0) return [];
  let best = cycles[0];
  for (const c of cycles) if (c.length > best.length) best = c;
  return best.map((v) => state.pattern.verticesCoords[v]);
}

const initialPattern = defaultExample?.build() ?? blankSheet();

export const useStore = create<AppState>((set, get) => ({
  pattern: initialPattern,
  analysis: analyzePattern(initialPattern),
  past: [],
  future: [],
  lastAction: 'Loaded example',

  tool: 'crease',
  selection: [],
  hover: null,
  inspectedEdge: null,
  inspectedVertex: null,

  snap: defaultSnap(),
  view: defaultView(),
  sim: defaultSim(),

  pin: null,
  frame: null,
  fps: 0,
  notices: [],
  learnTopic: null,

  commit: (pattern, label) => {
    const state = get();
    const past = [...state.past, { pattern: clonePattern(state.pattern), label: state.lastAction }];
    if (past.length > HISTORY_LIMIT) past.shift();
    set({
      pattern,
      analysis: analyzePattern(pattern),
      past,
      future: [],
      lastAction: label,
      selection: [],
      inspectedEdge: null,
      inspectedVertex: null,
    });
  },

  replaceWithoutHistory: (pattern) => {
    set({ pattern, analysis: analyzePattern(pattern) });
  },

  undo: () => {
    const state = get();
    if (state.past.length === 0) return;
    const previous = state.past[state.past.length - 1];
    set({
      pattern: previous.pattern,
      analysis: analyzePattern(previous.pattern),
      past: state.past.slice(0, -1),
      future: [{ pattern: clonePattern(state.pattern), label: state.lastAction }, ...state.future],
      lastAction: previous.label,
      selection: [],
      inspectedEdge: null,
      inspectedVertex: null,
    });
  },

  redo: () => {
    const state = get();
    if (state.future.length === 0) return;
    const next = state.future[0];
    set({
      pattern: next.pattern,
      analysis: analyzePattern(next.pattern),
      past: [...state.past, { pattern: clonePattern(state.pattern), label: state.lastAction }],
      future: state.future.slice(1),
      lastAction: next.label,
      selection: [],
      inspectedEdge: null,
      inspectedVertex: null,
    });
  },

  setTool: (tool) => set({ tool, selection: [] }),
  setSelection: (selection) => set({ selection }),
  toggleSelection: (ref, additive) => {
    const state = get();
    const has = state.selection.some((s) => s.kind === ref.kind && s.index === ref.index);
    if (!additive) {
      set({ selection: has && state.selection.length === 1 ? [] : [ref] });
      return;
    }
    set({
      selection: has
        ? state.selection.filter((s) => !(s.kind === ref.kind && s.index === ref.index))
        : [...state.selection, ref],
    });
  },
  setHover: (hover) => set({ hover }),
  inspectEdge: (inspectedEdge) => set({ inspectedEdge, inspectedVertex: null }),
  inspectVertex: (inspectedVertex) => set({ inspectedVertex, inspectedEdge: null }),

  addCrease: (a, b, assignment) => {
    const state = get();
    const p = state.pattern;

    // A crease only exists where there is paper. Anything the user drags past
    // the edge of the sheet is trimmed off rather than left as stray geometry.
    const pieces = clipSegmentToPolygon(a, b, paperOutline(state));
    if (pieces.length === 0) {
      state.notify('warning', 'That crease lies entirely outside the paper, so nothing was added.');
      return;
    }

    const coords = [...p.verticesCoords];
    const edges: [number, number][] = [...p.edgesVertices];
    const assignments = [...p.edgesAssignment];
    const angles = [...p.edgesFoldAngle];
    for (const [from, to] of pieces) {
      coords.push(from, to);
      edges.push([coords.length - 2, coords.length - 1]);
      assignments.push(assignment);
      angles.push(null);
    }

    const result = planarize({
      verticesCoords: coords,
      edgesVertices: edges,
      edgesAssignment: assignments,
      edgesFoldAngle: angles,
      meta: p.meta,
    });
    state.commit(result.pattern, `Add ${assignment} crease`);
    if (result.report.assignmentConflicts.length > 0) {
      state.notify(
        'warning',
        `${result.report.assignmentConflicts.length} overlapping crease(s) had conflicting assignments; the existing one was kept.`,
      );
    }
  },

  assignSelection: (assignment) => {
    const state = get();
    const edges = state.selection.filter((s) => s.kind === 'edge').map((s) => s.index);
    if (edges.length === 0) return;
    const next = clonePattern(state.pattern);
    for (const e of edges) {
      next.edgesAssignment[e] = assignment;
      next.edgesFoldAngle[e] = null;
    }
    // Re-planarise so that boundary/cut changes update the topology.
    const result = planarize({
      verticesCoords: next.verticesCoords,
      edgesVertices: next.edgesVertices,
      edgesAssignment: next.edgesAssignment,
      edgesFoldAngle: next.edgesFoldAngle,
      meta: next.meta,
    });
    state.commit(result.pattern, `Assign ${assignment}`);
  },

  toggleMountainValley: () => {
    const state = get();
    const edges = state.selection.filter((s) => s.kind === 'edge').map((s) => s.index);
    if (edges.length === 0) return;
    const next = clonePattern(state.pattern);
    for (const e of edges) {
      const a = next.edgesAssignment[e];
      if (a === 'M') next.edgesAssignment[e] = 'V';
      else if (a === 'V') next.edgesAssignment[e] = 'M';
      else continue;
      const stored = next.edgesFoldAngle[e];
      if (stored !== null && stored !== undefined) next.edgesFoldAngle[e] = -stored;
    }
    state.commit(next, 'Toggle mountain/valley');
  },

  deleteSelection: () => {
    const state = get();
    const edgeSet = new Set(state.selection.filter((s) => s.kind === 'edge').map((s) => s.index));
    const vertexSet = new Set(
      state.selection.filter((s) => s.kind === 'vertex').map((s) => s.index),
    );
    if (edgeSet.size === 0 && vertexSet.size === 0) return;
    const p = state.pattern;
    const keep: number[] = [];
    for (let e = 0; e < p.edgesVertices.length; e++) {
      if (edgeSet.has(e)) continue;
      const [a, b] = p.edgesVertices[e];
      if (vertexSet.has(a) || vertexSet.has(b)) continue;
      keep.push(e);
    }
    const result = planarize({
      verticesCoords: p.verticesCoords,
      edgesVertices: keep.map((e) => p.edgesVertices[e]),
      edgesAssignment: keep.map((e) => p.edgesAssignment[e]),
      edgesFoldAngle: keep.map((e) => p.edgesFoldAngle[e]),
      meta: p.meta,
    });
    state.commit(result.pattern, 'Delete');
  },

  moveVertex: (index, to, commitChange) => {
    const state = get();
    const next = clonePattern(state.pattern);
    if (index < 0 || index >= next.verticesCoords.length) return;
    next.verticesCoords[index] = { x: to.x, y: to.y };
    if (!commitChange) {
      state.replaceWithoutHistory(next);
      return;
    }
    const result = planarize({
      verticesCoords: next.verticesCoords,
      edgesVertices: next.edgesVertices,
      edgesAssignment: next.edgesAssignment,
      edgesFoldAngle: next.edgesFoldAngle,
      meta: next.meta,
    });
    state.commit(result.pattern, 'Move vertex');
  },

  setEdgeTarget: (edge, radians) => {
    const state = get();
    const overrides = { ...state.sim.edgeTargetOverride };
    if (radians === null) delete overrides[edge];
    else overrides[edge] = clamp(radians, -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
    set({ sim: { ...state.sim, edgeTargetOverride: overrides } });
  },

  toggleEdgeLock: (edge) => {
    const state = get();
    const locked = state.sim.lockedEdges.includes(edge)
      ? state.sim.lockedEdges.filter((e) => e !== edge)
      : [...state.sim.lockedEdges, edge];
    const overrides = { ...state.sim.edgeTargetOverride };
    if (!state.sim.lockedEdges.includes(edge) && overrides[edge] === undefined) {
      // Lock at the angle the crease currently holds so nothing jumps.
      const current = state.frame?.edgeFoldAngles[edge];
      overrides[edge] = Number.isFinite(current ?? NaN)
        ? (current as number)
        : resolveTargetAngle(
            state.pattern.edgesAssignment[edge],
            state.pattern.edgesFoldAngle[edge],
          ) * state.sim.foldAmount;
    }
    set({ sim: { ...state.sim, lockedEdges: locked, edgeTargetOverride: overrides } });
  },

  newSheet: (width, height) => {
    const pattern = blankSheet(width, height, `${width} x ${height} sheet`);
    get().loadPattern(pattern, 'New sheet');
  },

  loadExample: (id) => {
    const example = EXAMPLES.find((e) => e.id === id);
    if (!example) return;
    const pattern = example.build();
    get().loadPattern(pattern, `Load "${example.name}"`);
    set((s) => ({ sim: { ...s.sim, mode: example.recommendedSolver } }));
  },

  loadPattern: (pattern, label) => {
    const state = get();
    state.commit(pattern, label);
    set((s) => ({
      sim: { ...s.sim, foldAmount: 0, edgeTargetOverride: {}, lockedEdges: [], playing: false },
    }));
  },

  setSnap: (patch) => set((s) => ({ snap: { ...s.snap, ...patch } })),
  setView: (patch) => set((s) => ({ view: { ...s.view, ...patch } })),
  setDebug: (patch) => set((s) => ({ view: { ...s.view, debug: { ...s.view.debug, ...patch } } })),
  setSim: (patch) => set((s) => ({ sim: { ...s.sim, ...patch } })),
  setFoldAmount: (amount) =>
    set((s) => ({ sim: { ...s.sim, foldAmount: clamp(amount, -1, 1) } })),
  resetSimulation: () =>
    set((s) => ({ sim: { ...s.sim, foldAmount: 0, playing: false } })),

  setPin: (pin) => set({ pin }),
  setFrame: (frame) => set({ frame }),
  setFps: (fps) => set({ fps }),

  notify: (severity, message) =>
    set((s) => ({
      notices: [...s.notices.filter((n) => n.message !== message), { id: noticeId++, severity, message }].slice(-4),
    })),
  dismissNotice: (id) => set((s) => ({ notices: s.notices.filter((n) => n.id !== id) })),

  engineParams: (): EngineParams => {
    const s = get();
    return {
      mode: s.sim.mode,
      foldAmount: s.sim.foldAmount,
      compliant: { ...s.sim.compliant, foldAmount: s.sim.foldAmount },
      rigid: s.sim.rigid,
      collisionEnabled: s.sim.collisionEnabled,
      edgeTargetOverride: s.sim.edgeTargetOverride,
      lockedEdges: s.sim.lockedEdges,
      // Dragging only makes sense in the compliant model: the rigid solver's
      // state is fold angles, and a positional pin is not expressible there.
      pins: s.pin && s.sim.mode === 'compliant' ? [s.pin] : [],
    };
  },
}));
